/* eslint-disable no-unused-vars, no-lone-blocks */
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useParams } from "react-router-dom";
import picturePlaceHolder from "../../../assets/images/picture_placeholder.svg";
import fileUploadCloud from "../../../assets/images/fileupload_cloud.svg";
import document_icon from "../../../assets/images/upload_document.svg";
import {
    formatDateIntoString,
    convertTo12HourFormat,
    getTodayDate
} from "../../../utils/dateAndTime";
import { DateTimeInput } from "../CreateEvent/CreateEventContent1";
import SocialMediaHandles from "../../SocialMediaHandles/SocialMediaHandles";
import Question from "../../ui/QuestionComponent";
import { Switch } from "@material-tailwind/react";
import { MARKETPLACE_SELLER_TYPES } from "../../../constants/constants";
import Button from "../../ui/Button";
import { updateEvent } from "../../../firebaseFunctions/updateEOEvent";
import LocationAutocomplete from "../../ui/LocationAutocomplete";

const EditEventDetails = ({
    eventData,
    handleCancelChanges,
    setIsLoading,
    setPopUpModalType,
    setIsModalOpen
}) => {
    const { t } = useTranslation();
    const { id } = useParams()
    const documentsRef = useRef(null);
    const coverPhotoRef = useRef(null)
    const [formState, setFormState] = useState({
        eventName: "",
        location: "",
        description: "",
        startDate: "",
        startTime: "",
        endDate: "",
        endTime: "",
        recurringEvent: false,
        applicationDeadline: "",
        loadInDate: "",
        loadInTime: "",
        loadOutDate: "",
        loadOutTime: "",
        eoEventWebsiteURL: "",
        eoEventFacebookAccount: "",
        eoEventInstagramAccount: "",
        eoEventTikTokAccount: "",
        selectedRecurringOption: "",
        eventCoverPhoto: "",
        eventDocuments: [],
        eventMap: null,
        sellerCapacity: "0",
        sellerTypes: [],
        otherSellerType: "",
        registrationFee: 0,
        typeOfQuestionList: [],
    });

    const [isEditingName, setIsEditingName] = useState(false);
    const [tempEventName, setTempEventName] = useState("");
    const [nameChangeInfo, setNameChangeInfo] = useState({
        changesRemaining: 3,
        lastChangeDate: null,
        nextResetDate: null
    });

    const [imagePreview, setImagePreview] = useState(null);
    const [isImageChanged, setIsImageChanged] = useState(false);

    useEffect(() => {
        setFormState((prevState) => ({
            ...prevState,
            ...eventData,
            socialMediaHandles: eventData.socialMediaHandles || prevState.socialMediaHandles,
        }));

        // Initialize temp name when event data loads
        setTempEventName(eventData.eventName || "");
        
        // Load name change history from localStorage or initialize it
        const storedNameChanges = localStorage.getItem(`nameChanges_${id}`);
        if (storedNameChanges) {
            const { changesRemaining, lastChangeDate, nextResetDate } = JSON.parse(storedNameChanges);
            // Check if 30 days have passed since last reset
            if (nextResetDate && new Date() >= new Date(nextResetDate)) {
                // Reset the counter
                setNameChangeInfo({
                    changesRemaining: 3,
                    lastChangeDate: null,
                    nextResetDate: null
                });
            } else {
                setNameChangeInfo({
                    changesRemaining,
                    lastChangeDate,
                    nextResetDate
                });
            }
        }

        if (eventData?.eventCoverPhoto) {
            setImagePreview(eventData.eventCoverPhoto);
        }
    }, [eventData, id]);

    const [addRegistrationFee, setAddRegistrationFee] = useState(eventData.addRegistrationFee ?? false);
    const [absorbServiceFee, setAbsorbServiceFee] = useState(eventData.absorbServiceFee || false);
    const [dateErrors, setDateErrors] = useState({
        endDate: "",
        applicationDeadline: "",
        loadInDate: "",
        loadOutDate: ""
    });
    const validateDateTime = (field, newValue) => {

        const today = getTodayDate();
        const {
            startDate,
            startTime,
            endDate,
            endTime,
            loadInDate,
            loadInTime,
            loadOutDate,
            loadOutTime,
            applicationDeadline,
        } = formState;

        switch (field) {
            case "startDate":
                if (new Date(newValue) < new Date(today)) {
                    return "Event start date cannot be before today";
                }
                break;

            case "endDate":
                if (startDate) {
                    const startDateTime = new Date(`${startDate} ${startTime || "00:00"}`);
                    const endDateTime = new Date(`${newValue} ${endTime || "23:59"}`);
                    if (startDateTime >= endDateTime) {
                        return "End date & time must be after start date & time";
                    }
                }
                break;

            case "loadInDate":
                if (startDate) {
                    const loadInDateTime = new Date(`${newValue} ${loadInTime || "00:00"}`);
                    const eventStartDateTime = new Date(`${startDate} ${startTime || "00:00"}`);
                    if (loadInDateTime > eventStartDateTime) {
                        return "Load in must be on or before event start time";
                    }
                }
                break;

            case "loadOutDate":
                if (loadInDate) {
                    const loadInDateTime = new Date(`${loadInDate} ${loadInTime || "00:00"}`);
                    const loadOutDateTime = new Date(`${newValue} ${loadOutTime || "23:59"}`);
                    if (loadOutDateTime <= loadInDateTime) {
                        return "Load out must be after load in time";
                    }
                }
                if (endDate) {
                    const loadOutDateTime = new Date(`${newValue} ${loadOutTime || "23:59"}`);
                    const eventEndDateTime = new Date(`${endDate} ${endTime || "23:59"}`);
                    if (loadOutDateTime < eventEndDateTime) {
                        return "Load out must be on or after event end time";
                    }
                }
                break;

            case "applicationDeadline":
                if (startDate && new Date(newValue) > new Date(startDate)) {
                    return "Application deadline must be on or before event start";
                }
                break;

            default:
                return "";
        }

        return "";
    };

    const handleDateTimeChange = (field, newValue) => {
        // Update formState
        setFormState((prevState) => ({
            ...prevState,
            [field]: newValue,
        }));

        // Validate the updated value
        const error = validateDateTime(field, newValue);

        // Update dateErrors state
        setDateErrors((prev) => ({
            ...prev,
            [field]: error,
        }));
    };

    // Generalized Change Handler
    const handleChange = (field) => (e) => {
        const value = e.target.value;

        setFormState((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    //state handlers
    const formStateHandlers = {
        eventName: handleChange("eventName"),
        location: handleChange("location"),
        description: handleChange("description"),
        sellerCapacity: handleChange("sellerCapacity"),
        registrationFee: handleChange("registrationFee"),
        startDate: (e) => handleDateTimeChange("startDate", e.target.value),
        startTime: handleChange("startTime"),
        endDate: (e) => handleDateTimeChange("endDate", e.target.value),
        endTime: handleChange("endTime"),
        recurringEvent: () =>
            setFormState((prev) => ({ ...prev, recurringEvent: !prev.recurringEvent })),
        applicationDeadline: handleChange("applicationDeadline"),

        // For date-time inputs like loadInDate, loadInTime, loadOutDate, loadOutTime
        loadInDate: (e) => handleDateTimeChange("loadInDate", e.target.value),
        loadInTime: (e) => handleDateTimeChange("loadInTime", e.target.value),
        loadOutDate: (e) => handleDateTimeChange("loadOutDate", e.target.value),
        loadOutTime: (e) => handleDateTimeChange("loadOutTime", e.target.value),

        eoEventWebsiteURL: handleChange("eoEventWebsiteURL"),
        eoEventFacebookAccount: handleChange("eoEventFacebookAccount"),
        eoEventInstagramAccount: handleChange("eoEventInstagramAccount"),
        eoEventTikTokAccount: handleChange("eoEventTikTokAccount"),
    };
    const handleUploadImageClick = () => {
        coverPhotoRef.current.click();
    };
    const marketplaceSellerTypes = MARKETPLACE_SELLER_TYPES;

    const handleSelectAll = (e) => {
        setFormState((prev) => ({
            ...prev,
            selectedSellerType: e.target.checked ? marketplaceSellerTypes : [],
        }));
    };

    const handleSelectType = (e) => {
        const value = e.target.value;
        setFormState((prev) => ({
            ...prev,
            sellerTypes: prev.sellerTypes.includes(value)
                ? prev.sellerTypes.filter((item) => item !== value)
                : [...prev.sellerTypes, value],
        }));
    };
    const handleOther = (e) => {
        setFormState((prev) => ({
            ...prev,
            otherSellerType: e.target.value,
        }));
    };

    const handleFileUpload = (event) => {
        const files = event.target.files;
        const newDocuments = Array.from(files).map((file) => ({
            fileName: file.name,
            file: file,
        }));

        setFormState((prev) => ({
            ...prev,
            eventDocuments: [...prev.eventDocuments, ...newDocuments],
        }));
    };
    const handleFileRemove = (index) => {
        setFormState((prev) => ({
            ...prev,
            eventDocuments: prev.eventDocuments.filter((_, i) => i !== index),
        }));
        documentsRef.current.value = "";
    };
    const handleEventMapRemove = () => {
        setFormState((prev) => ({
            ...prev,
            eventMap: null,
        }));
    };

    {/* Questions */ }
    const handleQuestionChange = (index, updatedAnswer) => {
        setFormState((prevData) => {
            const updatedQuestions = prevData.typeOfQuestionList.map((q, i) =>
                i === index ? { ...q, answer: updatedAnswer } : q
            );
            return {
                ...prevData,
                typeOfQuestionList: updatedQuestions,
            };
        });
    };

    const handleSaveChanges = async () => {
        setIsLoading(true);
        setIsModalOpen(true);
        try {
            const updatedEventData = {
                ...formState,
                eventId: id,
                // Ensure all required fields are present
                userId: eventData.userId,
                deadlineDate: formState.applicationDeadline || eventData.deadlineDate,
                deadlineTime: formState.applicationDeadline ? "23:59" : eventData.deadlineTime,
                loadoutDate: formState.loadOutDate || eventData.loadoutDate,
                loadoutTime: formState.loadOutTime || eventData.loadoutTime,
                socialMediaHandles: {
                    website: formState.eoEventWebsiteURL || "",
                    facebook: formState.eoEventFacebookAccount || "",
                    instagram: formState.eoEventInstagramAccount || "",
                    tiktok: formState.eoEventTikTokAccount || ""
                }
            };

            // Ensure sellerTypes is an array
            if (!Array.isArray(updatedEventData.sellerTypes)) {
                updatedEventData.sellerTypes = [];
            }

            // Ensure sellerCapacity is a number
            if (typeof updatedEventData.sellerCapacity === 'string') {
                updatedEventData.sellerCapacity = parseInt(updatedEventData.sellerCapacity, 10) || 0;
            }

            const result = await updateEvent(updatedEventData);

            if (result.success) {
                setPopUpModalType("save changes success");
                setIsModalOpen(true);
            } else {
                console.error("Failed to update event:", result.message);
                setPopUpModalType("save changes failure");
                setIsModalOpen(true);
            }
        } catch (error) {
            console.error("Error while saving changes:", error);
            setPopUpModalType("save changes failure");
            setIsModalOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCoverPhotoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Check file type
            if (!file.type.startsWith('image/')) {
                setPopUpModalType("invalid file type");
                setIsModalOpen(true);
                return;
            }

            // Check file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                setPopUpModalType("file too large");
                setIsModalOpen(true);
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);

            setFormState(prevState => ({
                ...prevState,
                eventCoverPhoto: file
            }));
            setIsImageChanged(true);
        }
    };

    const handleRemoveImage = () => {
        setImagePreview(null);
        setFormState(prevState => ({
            ...prevState,
            eventCoverPhoto: null
        }));
        setIsImageChanged(true);
        if (coverPhotoRef.current) {
            coverPhotoRef.current.value = '';
        }
    };

    const handleRegistrationFeeToggle = () => {
        if (addRegistrationFee) {
            // If turning off registration fee, show confirmation
            setPopUpModalType("confirm free event");
            setIsModalOpen(true);
        } else {
            // If turning on registration fee, just update state
            setAddRegistrationFee(true);
        }
    };

    const handleConfirmFreeEvent = () => {
        setAddRegistrationFee(false);
        setFormState(prev => ({
            ...prev,
            registrationFee: 0
        }));
        setIsModalOpen(false);
    };

    const handleCancelFreeEvent = () => {
        setIsModalOpen(false);
    };

    const handleNameEdit = () => {
        if (nameChangeInfo.changesRemaining === 0) {
            const nextReset = new Date(nameChangeInfo.nextResetDate);
            const daysUntilReset = Math.ceil((nextReset - new Date()) / (1000 * 60 * 60 * 24));
            setPopUpModalType("name change limit");
            setIsModalOpen(true);
            return;
        }
        setIsEditingName(true);
    };

    const handleNameSave = () => {
        if (tempEventName !== formState.eventName) {
            const now = new Date();
            const nextReset = nameChangeInfo.nextResetDate 
                ? new Date(nameChangeInfo.nextResetDate)
                : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

            const updatedChanges = {
                changesRemaining: nameChangeInfo.changesRemaining - 1,
                lastChangeDate: now.toISOString(),
                nextResetDate: nextReset.toISOString()
            };

            setNameChangeInfo(updatedChanges);
            localStorage.setItem(`nameChanges_${id}`, JSON.stringify(updatedChanges));

            setFormState(prev => ({
                ...prev,
                eventName: tempEventName
            }));
        }
        setIsEditingName(false);
    };

    const handleNameCancel = () => {
        setTempEventName(formState.eventName);
        setIsEditingName(false);
    };

    return (
        <div className="px-8 w-full">
            <p className="text-xl font-medium ml-5">{t('eventDetails.editDetailsTitle')}</p>
            <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-8 mt-4">
                <div>
                    {/*cover image*/}
                    <div className="space-y-4">
                        <label className="block text-sm font-medium text-grey-900">
                            {t('createEvent.eventCoverPhoto')}
                        </label>
                        <div className="relative group">
                            <img
                                className="h-[200px] w-full object-cover rounded-lg"
                                src={imagePreview || picturePlaceHolder}
                                alt="Event cover"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-300 rounded-lg flex items-center justify-center">
                                <div className="hidden group-hover:flex gap-4">
                                    <button
                                        onClick={() => coverPhotoRef.current.click()}
                                        className="bg-white text-gray-800 px-4 py-2 rounded-lg text-sm hover:bg-gray-100 transition-colors duration-200"
                                    >
                                        {t('eventDetails.changePhotoButton')}
                                    </button>
                                    {imagePreview && (
                                        <button
                                            onClick={handleRemoveImage}
                                            className="bg-red-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-600 transition-colors duration-200"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <input
                            ref={coverPhotoRef}
                            type="file"
                            className="hidden"
                            onChange={handleCoverPhotoChange}
                            accept="image/*"
                        />
                        <p className="text-sm text-grey-600">
                            {t('eventDetails.coverPhotoRecommendedSize')}
                        </p>
                        {isImageChanged && (
                            <p className="text-sm text-[#B22A2B]">
                                {t('eventDetails.coverPhotoSaveReminder')}
                            </p>
                        )}
                    </div>

                    {/* Event Name */}
                    <div className="flex items-center gap-2 font-medium mt-6">
                        <div className="text-2xl">
                            <ion-icon name="text-outline" className="text-xl font-medium"></ion-icon>
                        </div>
                        <span className="font-medium text-md text-grey-900">{t('createEvent.eventName')}</span>
                        <span className="text-sm text-grey-600">
                            ({nameChangeInfo.changesRemaining} {t('eventDetails.changesRemainingLabel')})
                        </span>
                    </div>
                    <div className="md:ml-6 w-full relative">
                        <input
                            type="text"
                            value={isEditingName ? tempEventName : formState.eventName}
                            onChange={(e) => isEditingName && setTempEventName(e.target.value)}
                            onClick={!isEditingName ? handleNameEdit : undefined}
                            className={`w-full p-3 border ${
                                isEditingName ? 'border-[#B22A2B]' : 'border-grey-300'
                            } rounded-lg focus:ring-1 focus:ring-[#B22A2B] focus:border-[#B22A2B]`}
                            placeholder={eventData.eventName || "Enter event name"}
                            readOnly={!isEditingName}
                        />
                        {isEditingName && (
                            <div className="absolute right-2 top-2 flex gap-2">
                                <button
                                    onClick={handleNameCancel}
                                    className="px-3 py-1 text-sm text-grey-600 hover:text-grey-800"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    onClick={handleNameSave}
                                    className="px-3 py-1 bg-[#B22A2B] text-white text-sm rounded hover:bg-[#8B2122]"
                                >
                                    {t('common.save')}
                                </button>
                            </div>
                        )}
                        {nameChangeInfo.changesRemaining < 3 && !isEditingName && (
                            <p className="text-sm text-grey-600 mt-1">
                                {t('eventDetails.nameChangesReset')} {new Date(nameChangeInfo.nextResetDate).toLocaleDateString()}
                            </p>
                        )}
                    </div>

                    {/*start date*/}
                    <div className="flex items-center gap-2 font-medium mt-6">
                        <div className="text-2xl">
                            <ion-icon name="calendar-outline" className="text-xl font-medium"></ion-icon>
                        </div>
                        <span className="font-medium text-md text-grey-900">{formatDateIntoString(formState?.startDate)}</span>
                    </div>

                    {/* Event Start and End Date & Time */}
                    <div className="w-full flex flex-col gap-4 md:gap-8 mt-4 md:ml-6">
                        {/* Event Start Date & Time */}
                        <DateTimeInput
                            label={t('createEvent.eventStartDateTime')}
                            dateValue={formState.startDate}
                            onDateChange={formStateHandlers.startDate}
                            timeValue={formState.startTime}
                            onTimeChange={formStateHandlers.startTime}
                            error={dateErrors.startDate}
                            dateMin={getTodayDate()} // Use the minimum date logic if applicable
                        />

                        {/* Event End Date & Time */}
                        <DateTimeInput
                            label={t('createEvent.eventEndDateTime')}
                            dateValue={formState.endDate}
                            onDateChange={formStateHandlers.endDate}
                            timeValue={formState.endTime}
                            onTimeChange={formStateHandlers.endTime}
                            error={dateErrors.endDate}
                        />
                    </div>

                    {/*location*/}
                    <div className="flex items-center gap-2 font-medium mt-6">
                        <div className="text-2xl">
                            <ion-icon name="location-outline" className="text-xl font-medium"></ion-icon>
                        </div>
                        <span className="font-medium text-md text-grey-900">{t('createEvent.location')}</span>
                    </div>
                    <div className="md:ml-6 w-full">
                        <LocationAutocomplete
                            value={formState.location}
                            onChange={(value) => {
                                setFormState(prev => ({ ...prev, location: value }));
                            }}
                            onSelect={(location) => {
                                if (location) {
                                    setFormState(prev => ({
                                        ...prev,
                                        location: location.address,
                                        locationDetails: {
                                            city: location.city,
                                            state: location.state,
                                            country: location.country,
                                            postcode: location.postcode,
                                            lat: location.lat,
                                            lon: location.lon,
                                        }
                                    }));
                                }
                            }}
                            placeholder="Search for venue or address..."
                        />
                    </div>

                    {/*registration deadline*/}
                    <div className="flex items-center gap-2 font-medium mt-6">
                        <div className="text-2xl">
                            <ion-icon name="calendar-outline" className="text-xl font-medium"></ion-icon>
                        </div>
                        <span className="font-medium text-md text-grey-900">{t('eventDetails.registrationDeadlineLabel')}</span>
                    </div>
                    <div className="w-full flex flex-col gap-4 md:gap-8 md:ml-6">
                        {/* Application Deadline */}
                        <input
                            type="date"
                            value={formState.applicationDeadline}
                            onChange={formStateHandlers.applicationDeadline}
                            className="flex-1 p-3 border border-grey-300 rounded-lg"
                        />
                        {dateErrors.applicationDeadline && (
                            <p className="text-error-500 text-sm mt-1">
                                {dateErrors.applicationDeadline}
                            </p>
                        )}
                    </div>
                    {dateErrors.applicationDeadline && (
                        <p className="text-error-500 text-sm mt-1">{dateErrors.applicationDeadline}</p>
                    )}


                    {/*seller capacity*/}
                    <div className="flex items-center gap-2 font-medium mt-6">
                        <div className="text-2xl">
                            <ion-icon name="person-outline" className="text-xl font-medium"></ion-icon>
                        </div>
                        <span className="font-medium text-md text-grey-900">{t('createEvent.sellerCapacity')}</span>
                    </div>
                    <div className="md:ml-6 w-full">
                        <input
                            type="number"
                            value={formState.sellerCapacity}
                            onChange={formStateHandlers.sellerCapacity}
                            className="w-full p-3 border border-grey-300 rounded-lg"
                            placeholder={formState.sellerCapacity}
                        />
                    </div>

                    <div className="w-full flex flex-col gap-4 md:gap-8 mt-6">
                        {/* Load In Date & Time */}
                        <DateTimeInput
                            label={t('createEvent.loadInDateTime')}
                            dateValue={formState.loadInDate}
                            onDateChange={formStateHandlers.loadInDate}
                            timeValue={formState.loadInTime}
                            onTimeChange={formStateHandlers.loadInTime}
                            error={dateErrors.loadInDate}
                        />

                        {/* Load Out Date & Time */}
                        <DateTimeInput
                            label={t('createEvent.loadOutDateTime')}
                            dateValue={formState.loadOutDate}
                            onDateChange={formStateHandlers.loadOutDate}
                            timeValue={formState.loadOutTime}
                            onTimeChange={formStateHandlers.loadOutTime}
                            error={dateErrors.loadOutDate}
                        />
                    </div>

                    {/*website*/}
                    <p className="font-medium text-md text-grey-900 mt-6">{t('eventDetails.websiteLabel')}</p>
                    <p className="text-sm text-grey-700">{eventData?.eoEventWebsiteURL}</p>
                    {/*socials*/}
                    <p className="font-medium text-md text-grey-900 mt-6">{t('eventDetails.socialMediaLabel')}</p>
                    <SocialMediaHandles handles={eventData.socialMediaHandles} />
                </div>
                <div className="md:col-span-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="md:mx-12">
                            {/*registration fee*/}
                            <div className="flex items-center gap-2 font-medium mt-4">
                                <div className="text-2xl">
                                    <ion-icon name="card-outline" className="text-xl font-medium"></ion-icon>
                                </div>
                                <span className="font-medium text-md text-grey-900">{t('eventDetails.registrationFeeLabel')}</span>
                            </div>
                            <div className="md:ml-6 w-full">
                                <input
                                    type="number"
                                    value={formState.registrationFee}
                                    onChange={formStateHandlers.registrationFee}
                                    className="w-full p-3 border border-grey-300 rounded-lg"
                                    placeholder={formState.registrationFee}
                                />
                            </div>
                        </div>
                        <div className="md:mx-12">
                            {/*registration fee toggle*/}
                            <div className="flex items-start justify-start flex-col mt-4 gap-4">
                                <label
                                    htmlFor="addRegistrationFee"
                                    className="block text-sm font-medium text-grey-900"
                                >
                                    {t('eventDetails.addRegistrationFeeLabel')}
                                </label>
                                <Switch
                                    id="addRegistrationFee"
                                    className="h-full w-full checked:bg-pebble-red"
                                    containerProps={{
                                        className: "w-11 h-6 rounded-xs",
                                    }}
                                    circleProps={{
                                        className: "before:hidden left-0.5 border-none",
                                    }}
                                    checked={addRegistrationFee}
                                    onChange={handleRegistrationFeeToggle}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="md:m-12">
                        {/*About Evevnt*/}
                        <p className="font-medium text-md text-grey-900 mt-6">{t('eventDetails.aboutLabel')}</p>
                        <p className="text-sm font-normal text-grey-700">
                            {eventData.description}
                        </p>

                        {/*event documents*/}
                        <div className="mt-4 grid grid-cols1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            <div className="flex items-center justify-center" ref={documentsRef} >
                                <div className="flex w-auto bg-grey-25 items-center justify-center">
                                    <label
                                        htmlFor="documents-upload"
                                        className="flex flex-col items-center justify-center w-full py-4 border-2 border-dashed border-grey-300 rounded-lg cursor-pointer hover:border-grey-500 p-6"
                                    >
                                        <img src={fileUploadCloud} alt="fileUploadCloud" />
                                        <div className="flex flex-col items-center justify-center pt-5 pb-6 gap-2">
                                            <p className="mb-2 text-sm text-grey-900">
                                                <span className="font-medium whitespace-nowrap">
                                                    {t('eventDetails.uploadDocumentsHere')}
                                                </span>
                                            </p>
                                        </div>
                                        <input
                                            ref={documentsRef}
                                            id="documents-upload"
                                            type="file"
                                            className="hidden"
                                            onChange={handleFileUpload}
                                            multiple
                                        />
                                    </label>
                                </div>
                            </div>
                            {formState?.eventDocuments?.map((document, index) => (
                                <div key={index} className="border border-grey-300 rounded-2xl p-8 flex flex-col items-center justify-center">
                                    <img
                                        src={document_icon}
                                        alt=""
                                        className="h-[40px] w-[40px]"
                                    />
                                    <p className="pt-2 text-sm font-medium text-success-700 whitespace-nowrap">
                                        {document?.fileName.length > 15
                                            ? `${document.fileName.slice(0, 15)}...`
                                            : document.fileName}
                                    </p>
                                    <button
                                        onClick={() => handleFileRemove(index)}
                                        className="px-6 py-3 text-error-700 text-sm font-medium underline underline-offset-4"
                                    >
                                        {t('createEvent.remove')}
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Required documents */}
                        <div className="flex gap-2 font-medium mt-6">
                            <div className="space-y-1">
                                <span className="font-medium text-md text-grey-900">
                                    {t('eventDetails.requiredDocumentation')}
                                </span>
                                <p className="text-sm font-normal text-grey-700">
                                    <ul className="pl-5 list-solid-dot">
                                        {eventData.documents.map((doc, index) => (
                                            <li key={index}>{doc.documentName}</li>
                                        ))}
                                    </ul>
                                </p>
                            </div>
                        </div>

                        {/* Marketplace Vendor Types */}
                        <div className="space-y-4 mt-6">
                            <label htmlFor="sellerCapacity" className="block text-sm font-medium text-grey-900">
                                {t('createEvent.vendorTypes')}
                            </label>
                            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="selectAll"
                                        onChange={handleSelectAll}
                                        checked={
                                            formState.sellerTypes.length === marketplaceSellerTypes.length
                                        }
                                        className="h-4 w-4 border-grey-300 rounded accent-primary-600"
                                    />
                                    <label htmlFor="selectAll" className="text-xs text-grey-900">
                                        {t('createEvent.selectAll')}
                                    </label>
                                </div>
                                {marketplaceSellerTypes.map((type) => (
                                    <div key={type} className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            value={type}
                                            onChange={handleSelectType}
                                            checked={formState.sellerTypes.includes(type)}
                                            className="h-4 w-4 border-grey-300 rounded accent-primary-600"
                                        />
                                        <label className="text-xs text-grey-900">{type}</label>
                                    </div>
                                ))}
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="otherSellerType"
                                        onChange={handleOther}
                                        className="h-4 w-4 border-grey-300 rounded"
                                    />
                                    <label
                                        htmlFor="otherSellerType"
                                        className="flex w-7/12 text-xs text-grey-900 justify-center items-center gap-2"
                                    >
                                        {t('createEvent.other')}
                                        <input
                                            type="text"
                                            id="other"
                                            value={formState.otherSellerType}
                                            onChange={(e) => handleOther(e)}
                                            className="h-full w-full border border-grey-300 rounded !accent-primary-600"
                                        />
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <p className="text-xl font-medium ml-5 mt-12">{t('eventDetails.additionalDetailsTitle')}</p>

            {/*event map*/}
            <label className="block text-sm font-medium text-grey-900 mt-4">
                {t('createEvent.eventMap')}
            </label>
            <div className="mt-4 flex gap-4">
                <EventMap setFormState={setFormState} />
                {formState?.eventMap && <div className="border border-grey-300 rounded-2xl p-8 flex flex-col items-center justify-center w-[166px]">
                    <img
                        src={document_icon}
                        alt=""
                        className="h-[40px] w-[40px]"
                    />
                    <p className="pt-2 text-sm font-medium text-success-700 whitespace-nowrap">
                        {formState?.eventMap?.fileName?.length > 11
                            ? `${formState?.eventMap.fileName.slice(0, 11)}...`
                            : formState?.eventMap?.fileName}
                    </p>
                    <button
                        onClick={handleEventMapRemove}
                        className="px-6 py-3 text-error-700 text-sm font-medium underline underline-offset-4"
                    >
                        {t('createEvent.remove')}
                    </button>
                </div>}
            </div>
            {/*additional questions*/}
            <label className="block text-sm font-medium text-grey-900 mt-8">
                {t('eventDetails.questionsForVendors')}
            </label>
            {formState?.typeOfQuestionList?.map((question, index) => (
                <Question
                    key={index}
                    question={question}
                    onChange={handleQuestionChange}
                    //errors={errors}
                    index={index}
                />
            ))}
            <div className="flex justify-end mt-6 gap-6 w-full">
                <Button onClick={handleCancelChanges} variant="transparent">
                    {t('eventDetails.cancelChanges')}
                </Button>
                <Button onClick={handleSaveChanges} >
                    {t('eventDetails.saveChanges')}
                </Button>
            </div>
        </div>
    );
};

export default EditEventDetails;


const EventMap = ({ setFormState }) => {
    const { t } = useTranslation();
    const handleEventMapUpload = (e) => {
        const map = e.target.files[0];
        if (map) {
            setFormState((prev) => ({
                ...prev,
                eventMap: {
                    fileName: map.name,
                    file: map,
                },
            }));
        }
    };
    return (
        <div className="flex items-center justify-center">
            <div className="flex w-auto bg-grey-25 items-center justify-center">
                <label
                    htmlFor="file-upload"
                    className="flex flex-col items-center justify-center w-full py-4 border-2 border-dashed border-grey-300 rounded-lg cursor-pointer hover:border-grey-500 p-6"
                >
                    <img src={fileUploadCloud} alt="fileUploadCloud" />
                    <div className="flex flex-col items-center justify-center pt-5 pb-6 gap-2">
                        <p className="mb-2 text-sm text-grey-900">
                            <span className="font-medium whitespace-nowrap">
                                {t('createEvent.uploadEventMap')}
                            </span>
                        </p>
                    </div>
                    <input
                        //ref={eventMapRef}
                        id="file-upload"
                        type="file"
                        className="hidden"
                        onChange={handleEventMapUpload}
                        multiple
                    />
                </label>
            </div>
        </div>
    );
};